import i18n from '../../i18n';
import type { AppLocale } from '../../i18n/types';
import { DEFAULT_APP_LOCALE, isAppLocale } from '../../i18n/types';

export type MessagesClientsTemplateKey = 'generic' | 'services' | 'products' | 'mixed';

export const MESSAGES_CLIENTS_TEMPLATE_KEYS: MessagesClientsTemplateKey[] = [
  'generic',
  'services',
  'products',
  'mixed',
];

function normalizeTemplate(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Template système Messages Clients pour une locale. */
export function getSystemMessagesClientsTemplate(
  key: MessagesClientsTemplateKey,
  locale?: AppLocale
): string {
  const lng = isAppLocale(locale) ? locale : (i18n.language as AppLocale) || DEFAULT_APP_LOCALE;
  return String(
    i18n.t(`templates.${key}`, {
      ns: 'messagesClients',
      lng,
      defaultValue: '',
    })
  );
}

export function getMessagesClientsFallbackName(locale?: AppLocale): string {
  const lng = isAppLocale(locale) ? locale : (i18n.language as AppLocale) || DEFAULT_APP_LOCALE;
  return String(
    i18n.t('fallbackClientName', {
      ns: 'messagesClients',
      lng,
      defaultValue: 'cher client',
    })
  );
}

/**
 * true si le texte stocké diffère des défauts système FR et EN
 * (donc personnalisé par le business → ne pas retraduire).
 */
export function isUserContentTemplate(
  stored: string | undefined | null,
  systemKey: MessagesClientsTemplateKey
): boolean {
  if (!stored || !String(stored).trim()) return false;
  const normalized = normalizeTemplate(stored);
  const fr = normalizeTemplate(getSystemMessagesClientsTemplate(systemKey, 'fr'));
  const en = normalizeTemplate(getSystemMessagesClientsTemplate(systemKey, 'en'));
  return normalized !== fr && normalized !== en;
}

/** Résout le template à utiliser pour générer un message (locale courante). */
export function resolveMessagesClientsTemplate(
  systemKey: MessagesClientsTemplateKey,
  stored?: string | null
): string {
  if (stored && isUserContentTemplate(stored, systemKey)) {
    return stored;
  }
  return getSystemMessagesClientsTemplate(systemKey);
}

/** Map des 4 templates système pour DEFAULT_BUSINESS_CONFIGURATION (locale courante). */
export function getDefaultMessagesClientsTemplatesRecord(): Record<
  MessagesClientsTemplateKey,
  string
> {
  return {
    generic: getSystemMessagesClientsTemplate('generic'),
    services: getSystemMessagesClientsTemplate('services'),
    products: getSystemMessagesClientsTemplate('products'),
    mixed: getSystemMessagesClientsTemplate('mixed'),
  };
}
